import { useState, useEffect } from "react";
import { Download, X, Share, Loader2, CheckCircle2, AlertTriangle, MoreVertical } from "lucide-react";
import { C } from "./data";
import { isStandalone, getMobileOS, isInAppBrowser } from "./device";
import { getInstallState, subscribeInstall, runInstall, clearInstalled } from "./pwaInstall";

const SNOOZE = "rq_install_snoozed_at";
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;
// Arriving from the email QR code: they scanned it to install, so never snooze or hide the card.
const scannedToInstall = () => {
  try { return new URLSearchParams(window.location.search).get("install") === "1"; }
  catch (e) { return false; }
};

const snoozed = () => {
  try {
    const at = Number(localStorage.getItem(SNOOZE) || 0);
    return at > 0 && Date.now() - at < SNOOZE_MS;
  } catch (e) { return false; }
};

export default function InstallPrompt() {
  const forced = scannedToInstall();
  const ios = getMobileOS() === "ios";
  const inApp = isInAppBrowser();
  const [state, setState] = useState(getInstallState);
  const [hidden, setHidden] = useState(() => !forced && snoozed());
  const [busy, setBusy] = useState(false);
  // iPhone and in-app browsers have no one-tap install, so show the steps straight away.
  const [manualHelp, setManualHelp] = useState(forced || ios || inApp);

  useEffect(() => subscribeInstall(setState), []);

  // Already on the home screen — nothing to show. "Installed once" is only a hint (the icon may have
  // been removed since), so offer a way back to the install steps; in an in-app browser ignore it.
  if (isStandalone()) return null;
  if (state.installed && !inApp && !forced) {
    return (
      <div data-testid="install-done" style={{ background: `${C.green}12`, border: `1px solid ${C.green}44`, borderRadius: 14, padding: 13, marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
        <CheckCircle2 size={18} color={C.green} />
        <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.4, flex: 1 }}>
          <b>ResQ1 is installed.</b> Open it from your home screen for instant help.
          <div>
            <button onClick={() => { clearInstalled(); setManualHelp(true); setHidden(false); }} data-testid="install-again"
              style={{ background: "none", border: 0, padding: 0, marginTop: 4, color: C.red, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
              Not on your home screen? Install it again →
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (hidden) return null;

  const dismiss = () => { try { localStorage.setItem(SNOOZE, String(Date.now())); } catch (e) { /* storage unavailable */ } setHidden(true); };
  const install = async () => {
    if (!state.deferred) { setManualHelp((v) => !v); return; }
    setBusy(true);
    const outcome = await runInstall();
    setBusy(false);
    // The browser only ever offers its dialog once — if they backed out, show the manual steps.
    if (outcome !== "accepted") setManualHelp(true);
  };

  // In an in-app browser (WhatsApp / Instagram / Facebook) you cannot add to the home screen at all.
  // Tell them to reopen in the real browser — this is what makes "the app never appeared" happen.
  if (inApp) {
    return (
      <div data-testid="install-inapp" style={{ background: C.navy, color: "#fff", borderRadius: 14, padding: 14, marginBottom: 18, position: "relative" }}>
        <button onClick={dismiss} data-testid="install-dismiss"
          style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,0.14)", border: 0, borderRadius: 999, color: "#fff", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <X size={13} />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 11, paddingRight: 22 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: C.amber, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <AlertTriangle size={20} color={C.navy} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Open ResQ1 in your browser to install</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", marginTop: 2, lineHeight: 1.45 }}>You opened this inside another app — the home-screen icon can only be added from {ios ? "Safari" : "Chrome"}.</div>
          </div>
        </div>
        <div style={{ marginTop: 10, background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: 12, fontSize: 12.5, lineHeight: 1.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}><MoreVertical size={14} /> 1. Tap the menu (<b>{ios ? "Share / ⋯" : "⋮"}</b>) in this window</div>
          <div style={{ marginBottom: 3 }}>2. Choose <b>Open in {ios ? "Safari" : "Chrome"}</b> (or copy the link into {ios ? "Safari" : "Chrome"})</div>
          <div>3. Then {ios ? <>tap <Share size={12} style={{ display: "inline", verticalAlign: "middle" }} /> <b>Share → Add to Home Screen</b></> : <>tap <b>⋮ → Install app</b></>}.</div>
        </div>
      </div>
    );
  }

  if (!state.deferred && !ios && !manualHelp && !forced) return null;

  return (
    <div data-testid="install-prompt" style={{ background: C.navy, color: "#fff", borderRadius: 14, padding: 14, marginBottom: 18, position: "relative" }}>
      <button onClick={dismiss} data-testid="install-dismiss"
        style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,0.14)", border: 0, borderRadius: 999, color: "#fff", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <X size={13} />
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 11, paddingRight: 22 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Download size={20} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Install ResQ1 for instant help</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 2, lineHeight: 1.4 }}>Add it to your home screen so your panic button is always one tap away.</div>
        </div>
      </div>
      <button onClick={install} disabled={busy} data-testid="install-cta"
        style={{ marginTop: 12, width: "100%", padding: 11, background: C.red, color: "#fff", border: 0, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.75 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        {busy ? (
          <><Loader2 size={15} data-testid="install-spinner" style={{ animation: "rqspin 0.9s linear infinite" }} /> Adding to your home screen…</>
        ) : !state.deferred ? "How to install" : "Add to home screen"}
      </button>
      {manualHelp && (
        <div data-testid="install-ios-help" style={{ marginTop: 10, background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: 12, fontSize: 12.5, lineHeight: 1.7 }}>
          {ios ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}><Share size={14} /> 1. Tap the <b>Share</b> icon in Safari</div>
              <div>2. Choose <b>Add to Home Screen</b>, then tap <b>Add</b>.</div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 3 }}>1. Tap the <b>⋮</b> menu at the top right of your browser</div>
              <div>2. Choose <b>Install app</b> (or <b>Add to Home screen</b>), then tap <b>Install</b>.</div>
            </>
          )}
        </div>
      )}
      <style>{"@keyframes rqspin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}
